import { useMemo } from 'react';
import { prominenceOf } from '../../poller/diff.js';
import type { DateSlot } from '../../poller/types.js';
import { FormatIcon } from '../components/FormatIcon.tsx';
import { Thumb } from '../components/Thumb.tsx';
import { deriveHome, homeSummary, wantedReleases } from '../lib/derive.js';
import { formatLabel } from '../lib/formats.js';
import { hasPassedLocally } from '../lib/localdate.js';
import { formatSince, headlineFor, headlineTitle } from '../lib/format.js';
import type { WatchEvent, WatchItem } from '../lib/model.js';
import { Chips } from './rows.tsx';

interface Props {
  items: readonly WatchItem[];
  events: readonly WatchEvent[];
  lastSeenAt: string | null;
  now: Date;
  onOpen: (item: WatchItem) => void;
  onAdd: () => void;
  onOpenSlate: () => void;
}

/**
 * Two questions: can I start something, and has anything moved.
 *
 * Everything that is merely *pending* now lives on the Slate page. This
 * screen is the one you open to be told whether it is worth reading further,
 * so on most days it should be short — and on a quiet day it is one line.
 */
export function Home({ items, events, lastSeenAt, now, onOpen, onAdd, onOpenSlate }: Props) {
  const model = useMemo(
    () => deriveHome({ items, events, lastSeenAt, now }),
    [items, events, lastSeenAt, now],
  );
  const summary = homeSummary(model);

  if (items.length === 0) {
    return (
      <div className="empty">
        <h2>Nothing on the slate yet.</h2>
        <p>
          Add a book, a film, a series — or the person who makes them, if you want everything
          they do next.
        </p>
        <p>
          <button type="button" className="primary-btn" onClick={onAdd}>
            Add the first thing
          </button>
        </p>
      </div>
    );
  }

  const looked = `last looked ${formatSince(lastSeenAt, now)}`;
  // The counts only earn their place when the two lists together are too long
  // to take in at a glance. Below that the cards are the summary.
  const meta = summary.counts ? `${summary.counts} · ${looked}` : looked;

  if (summary.quiet) {
    return (
      <>
        <div className="verdict">
          <h1>No changes.</h1>
          <span className="since">nothing ready, nothing moved · {looked}</span>
        </div>
        <SlateLink model={model} onOpenSlate={onOpenSlate} />
      </>
    );
  }

  return (
    <>
      {summary.ready > 0 && (
        <>
          <div className="verdict">
            <h1 className="head-ready">Ready.</h1>
            <span className="since">{meta}</span>
          </div>
          <div className="section cards2">
            {model.outNow.map((item) => (
              <button
                key={item.subscriptionId}
                className="card available"
                onClick={() => onOpen(item)}
              >
                <Thumb src={item.imageUrl} kind={item.kind} medium={item.medium} />
                <span className="card-body">
                  <span className="item-title">{item.title}</span>
                  {item.byline && <span className="byline">{item.byline}</span>}
                  <Chips item={item} />
                  <FormatRow item={item} slots={outSlots(item, now)} />
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {summary.updates > 0 && (
        <>
          <div className="verdict" style={{ paddingTop: summary.ready > 0 ? 30 : undefined }}>
            <h1 className="head-updates">Something changed.</h1>
            {/* Only carries the meta line when it is the first thing on the
                screen — repeating "last looked" twice reads as a glitch. */}
            {summary.ready === 0 && <span className="since">{meta}</span>}
          </div>
          <div className="section cards2">
            {model.changes.map(({ event, item }) => {
              const headline = headlineFor(event.type, event.payload);
              const quiet = prominenceOf(event.type) === 'quiet';
              return (
                <button key={event.id} className="card" onClick={() => onOpen(item)}>
                  <Thumb src={item.imageUrl} kind={item.kind} medium={item.medium} />
                  <span className="card-body">
                    <span className={quiet ? 'event-kind quiet' : 'event-kind'}>
                      {headline.label}
                    </span>
                    <span className="item-title">
                      {headlineTitle(event.type, event.payload, item.title)}
                    </span>
                    {item.byline && <span className="byline">{item.byline}</span>}
                    <Chips item={item} />
                    <span className="detail-line">{headline.detail}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <SlateLink model={model} onOpenSlate={onOpenSlate} />
    </>
  );
}

/**
 * The way through to everything that hasn't happened yet. It matters more now
 * that the timeline has moved off this screen: without it, a quiet home
 * screen would look like an empty app.
 */
function SlateLink({
  model,
  onOpenSlate,
}: {
  model: ReturnType<typeof deriveHome>;
  onOpenSlate: () => void;
}) {
  const dated = model.spine.filter((r) => r.kind === 'entry').length;
  const waiting = model.waiting.length;
  const total = dated + waiting;
  if (total === 0) return null;

  return (
    <>
      <div className="section" style={{ marginTop: 28 }}>
        <button type="button" className="wayto" onClick={onOpenSlate}>
          <span className="wayto-main">On the slate</span>
          <span className="wayto-meta">
            {dated > 0 ? `${dated} dated · ` : ''}
            {waiting} with no date
          </span>
          <span className="wayto-arrow" aria-hidden="true">
            →
          </span>
        </button>
      </div>

      <p className="attribution">
        This product uses the TMDb API but is not endorsed or certified by TMDb.
      </p>
    </>
  );
}

/** "Hardback · audiobook" — what you could actually go and get, with marks. */
function FormatRow({ item, slots }: { item: WatchItem; slots: readonly DateSlot[] }) {
  if (slots.length === 0) return <span className="detail-line">out now</span>;
  return (
    <span className="formatrow">
      {slots.map((slot) => (
        <span key={slot} className="format">
          <FormatIcon slot={slot} medium={item.medium} size={14} />
          {formatLabel(slot, item.medium)}
        </span>
      ))}
    </span>
  );
}

/**
 * The formats you can have today, in the forms you said you wanted.
 *
 * `hasPassedLocally`: the END of a date's range, so a book known only as
 * "2026" is not out in January — judged in the reader's own calendar, so
 * "out on the 4th" does not linger as pending into their 5th.
 */
function outSlots(item: WatchItem, now: Date): DateSlot[] {
  return Object.entries(wantedReleases(item))
    .filter(([, date]) => date != null && hasPassedLocally(date, now))
    .map(([slot]) => slot as DateSlot);
}
